import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import axios from '../util/Api.js';
import getConnectorPowerKw from '../util/ConnectorCalculatePower.js';
import { getLocationData } from '../util/getLocationData.js';

const httpProtocol = process.env.REACT_APP_SSL_ENABLED ? 'https' : 'http';
const currencies = {
  EUR: '€',
  USD: '$',
  TTD: 'TT$',
};

export default function Checkout() {
  const [state, setState] = React.useState({
    power_type: null,
    max_voltage: null,
    max_amperage: null,
    address: null,
    postalCode: null,
    city: null,
    state: null,
    country: null,
    operator: null,
    payment_terms_conditions: null,
    tariff_data: null,
    errMsg: null,
    taChecked: false,
    loading: false,
    initializing: true,
    status: 'UNKNOWN',
    modalVisible: false,
    paymentProvider: 'stripe',
    availableProviders: [],
    chargeMode: 'full',
    inputMode: 'amount',
    customAmount: '',
    customHours: '',
    customSoC: '',
  });
  const navigate = useNavigate();
  const intl = useIntl();
  const { evseId } = useParams();

  React.useEffect(() => {
    axios.get('checkouts/providers').then(({ data }) => {
      const providers = data?.providers || ['stripe'];
      setState((prev) => ({
        ...prev,
        availableProviders: providers,
        paymentProvider: providers[0] || 'stripe',
      }));
    }).catch(() => {});

    const setLocationData = (location) => {
      setState((prevState) => ({
        ...prevState,
        ...location,
        initializing: false,
      }));
    };
    if (evseId) {
      axios
        .get(`evses/${evseId}`)
        .then(async ({ data }) => {
          if (data.id) {
            const location_data = await getLocationData(data);
            setLocationData(location_data);
          } else {
            navigate('/', {
              state: {
                evseId: evseId,
                errMsg: data.message ? data.message : 'global.error.generic',
              },
            });
          }
        })
        .catch(() => {
          navigate('/', {
            state: { evseId: evseId, errMsg: 'global.error.generic' },
          });
        });
    } else {
      navigate('/');
    }
  }, [evseId, navigate]);

  const getSelectedAmount = () => {
    if (state.chargeMode === 'full') return null;
    if (state.chargeMode === 'soc') return null;
    if (state.inputMode === 'amount') {
      return parseFloat(state.customAmount) || null;
    }
    return getEstimatedCostFromHours();
  };

  const getEstimatedCostFromHours = () => {
    const hours = parseFloat(state.customHours) || 0;
    if (hours <= 0 || !state.tariff_data) return 0;
    const powerKw = getConnectorPowerKw(state.max_voltage, state.max_amperage, state.power_type);
    const energyCost = (state.tariff_data.price_kwh || 0) * powerKw * hours;
    const timeCost = (state.tariff_data.price_minute || 0) * 60 * hours;
    const sessionCost = state.tariff_data.price_session || 0;
    const subtotal = energyCost + timeCost + sessionCost;
    const total = subtotal * (state.tariff_data.tax_included ? 1 : 1 + (state.tariff_data.tax_rate || 0) / 100);
    return Math.ceil(total * 100) / 100;
  };

  const getEstimatedHoursFromAmount = () => {
    const amount = parseFloat(state.customAmount) || 0;
    if (amount <= 0 || !state.tariff_data) return 0;
    const powerKw = getConnectorPowerKw(state.max_voltage, state.max_amperage, state.power_type);
    const sessionCost = state.tariff_data.price_session || 0;
    const taxMultiplier = state.tariff_data.tax_included ? 1 : 1 + (state.tariff_data.tax_rate || 0) / 100;
    const costPerHour = ((state.tariff_data.price_kwh || 0) * powerKw + (state.tariff_data.price_minute || 0) * 60) * taxMultiplier;
    if (costPerHour <= 0) return 0;
    const available = amount - (sessionCost * taxMultiplier);
    if (available <= 0) return 0;
    const totalHours = available / costPerHour;
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  const onCheckout = () => {
    setState({ ...state, errMsg: null });
    if (!state.taChecked) {
      setState({ ...state, errMsg: intl.formatMessage({ id: 'checkout.error.tanotaccepted' }) });
      return;
    }
    const selectedAmount = getSelectedAmount();
    if (state.chargeMode === 'custom' && (!selectedAmount || selectedAmount < 0.5)) {
      setState({ ...state, errMsg: intl.formatMessage({ id: 'checkout.error.minamount' }) });
      return;
    }
    const targetSoC = state.chargeMode === 'soc' ? parseInt(state.customSoC) : null;
    if (state.chargeMode === 'soc' && (!targetSoC || targetSoC < 1 || targetSoC > 100)) {
      setState({ ...state, errMsg: intl.formatMessage({ id: 'checkout.error.invalidsoc' }) });
      return;
    }
    setState({ ...state, loading: true });
    axios
      .post(`checkouts/`, {
        evse_id: evseId,
        success_url: `${httpProtocol}://${window.location.host}/charging/${evseId}`,
        cancel_url: `${httpProtocol}://${window.location.host}/checkout/${evseId}`,
        selected_amount: selectedAmount,
        target_soc: targetSoC,
        payment_provider: state.paymentProvider,
      })
      .then(({ data }) => {
        if (data?.url) {
          window.location.replace(data.url);
        } else {
          setState({ ...state, loading: false, errMsg: intl.formatMessage({ id: 'global.error.generic' }) });
        }
      })
      .catch(() => {
        setState({ ...state, loading: false, errMsg: intl.formatMessage({ id: 'global.error.generic' }) });
      });
  };

  const get_price = (net_price) => {
    const price = net_price * (state.tariff_data?.tax_included ? 1 : 1 + (state.tariff_data?.tax_rate || 0) / 100);
    return price.toFixed(2);
  };

  const cur = currencies[state.tariff_data?.currency] || '';

  if (state.initializing) {
    return (
      <div className="min-h-screen w-full bg-brand-gradient flex items-center justify-center">
        <div className="text-white text-center">
          <i className="ri-loader-4-line text-4xl animate-spin"></i>
          <p className="mt-4 text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-brand-gradient flex flex-col">
      <div className="flex-1 w-full max-w-md mx-auto px-4 pt-14 pb-48">
        {/* Header card */}
        <div className="glass-card p-4 mb-6">
          <div className="flex justify-between items-center text-white text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-ev-charge-line text-accent text-lg"></i>
              <span className="font-bold">{state.station_name || evseId}</span>
            </div>
            <div className="flex items-center gap-3 text-white/60">
              <span>{state.power_type}</span>
              <span className="text-accent font-bold">
                {getConnectorPowerKw(state.max_voltage, state.max_amperage, state.power_type)} kW
              </span>
            </div>
          </div>
        </div>

        {/* Tariff info */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-price-tag-3-line text-accent"></i>
            <span className="text-white font-bold text-sm">{intl.formatMessage({ id: 'checkout.tariffinfo' })}</span>
          </div>
          <div className="glass-card divide-y divide-white/10">
            {state.tariff_data?.price_kwh > 0 && (
              <div className="flex justify-between px-4 py-3 text-sm">
                <span className="text-white/70">{intl.formatMessage({ id: 'checkout.pricekwh' })}</span>
                <span className="text-white font-semibold">{cur}{get_price(state.tariff_data?.price_kwh)}</span>
              </div>
            )}
            {state.tariff_data?.price_session > 0 && (
              <div className="flex justify-between px-4 py-3 text-sm">
                <span className="text-white/70">{intl.formatMessage({ id: 'checkout.pricesession' })}</span>
                <span className="text-white font-semibold">{cur}{get_price(state.tariff_data?.price_session)}</span>
              </div>
            )}
            {state.tariff_data?.idle_per_hour != null && <div className="px-4 py-3 text-sm text-white/70 border-t border-white/10"><p>Tariff (Idle): {cur}{state.tariff_data.idle_per_hour.toFixed(2)} per hour or part of an hour.</p><p>Starts at {state.tariff_data.idle_start_hour ?? 7}:00, after {state.tariff_data.idle_grace_min ?? 60} minutes grace (Central time). Maximum {cur}{state.tariff_data.max_daily_tariff ?? 24} per day; {state.tariff_data.max_session_tariff === 0 ? 'no session maximum' : 'maximum ' + cur + (state.tariff_data.max_session_tariff ?? 120) + ' per session'}.</p></div>}
<div className="px-4 py-2 text-xs text-white/40 text-center">
              {state.tariff_data?.tax_included ? 'Energy and idle fees include tax. No additional tax is added.' : intl.formatMessage({ id: 'checkout.inclvat' })}
            </div>
          </div>
        </div>

        {/* Payment provider */}
        {state.availableProviders.length > 1 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-bank-card-line text-accent"></i>
              <span className="text-white font-bold text-sm">{intl.formatMessage({ id: 'checkout.payment.title' })}</span>
            </div>
            <div className="space-y-2">
              {state.availableProviders.map((prov) => (
                <div
                  key={prov}
                  className={`radio-card ${state.paymentProvider === prov ? 'radio-card-selected' : 'radio-card-unselected'}`}
                  onClick={() => setState({ ...state, paymentProvider: prov })}
                >
                  <div className={`radio-dot ${state.paymentProvider === prov ? 'radio-dot-selected' : 'radio-dot-unselected'}`} />
                  <span className="text-white text-sm">
                    <i className={prov === 'stripe' ? 'ri-visa-line' : 'ri-secure-payment-line'} />{' '}
                    {intl.formatMessage({ id: `checkout.payment.${prov}` })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Charge mode */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-battery-charge-line text-accent"></i>
            <span className="text-white font-bold text-sm">{intl.formatMessage({ id: 'checkout.charge.title' })}</span>
          </div>
          <div className="space-y-2">
            {/* Full charge */}
            <div
              className={`radio-card ${state.chargeMode === 'full' ? 'radio-card-selected' : 'radio-card-unselected'}`}
              onClick={() => setState({ ...state, chargeMode: 'full' })}
            >
              <div className={`radio-dot ${state.chargeMode === 'full' ? 'radio-dot-selected' : 'radio-dot-unselected'}`} />
              <span className="text-white text-sm">
                {intl.formatMessage(
                  { id: 'checkout.charge.full' },
                  { amount: `${cur}${state.tariff_data?.authorization_amount}` },
                )}
              </span>
            </div>

            {/* Custom amount */}
            <div
              className={`radio-card flex-col items-start ${state.chargeMode === 'custom' ? 'radio-card-selected' : 'radio-card-unselected'}`}
              onClick={() => setState({ ...state, chargeMode: 'custom' })}
            >
              <div className="flex items-center">
                <div className={`radio-dot ${state.chargeMode === 'custom' ? 'radio-dot-selected' : 'radio-dot-unselected'}`} />
                <span className="text-white text-sm">{intl.formatMessage({ id: 'checkout.charge.custom' })}</span>
              </div>
              {state.chargeMode === 'custom' && (
                <div className="w-full mt-3 pl-8 space-y-3">
                  {/* Toggle */}
                  <div className="flex justify-center bg-white/10 rounded-full p-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setState({ ...state, chargeMode: 'custom', inputMode: 'amount' }); }}
                      className={`px-5 py-1.5 rounded-full text-sm font-bold transition-all ${state.inputMode === 'amount' ? 'bg-accent text-white' : 'text-white/60'}`}
                    >
                      {cur} {intl.formatMessage({ id: 'checkout.mode.amount' })}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setState({ ...state, chargeMode: 'custom', inputMode: 'hours' }); }}
                      className={`px-5 py-1.5 rounded-full text-sm font-bold transition-all ${state.inputMode === 'hours' ? 'bg-accent text-white' : 'text-white/60'}`}
                    >
                      <i className="ri-time-line"></i> {intl.formatMessage({ id: 'checkout.mode.hours' })}
                    </button>
                  </div>
                  <input
                    type="number"
                    min={state.inputMode === 'amount' ? '0.5' : '0.5'}
                    max={state.inputMode === 'hours' ? '24' : undefined}
                    step={state.inputMode === 'amount' ? '1' : '0.5'}
                    value={state.inputMode === 'amount' ? state.customAmount : state.customHours}
                    placeholder={state.inputMode === 'amount' ? cur : '0.5 - 24 h'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (state.inputMode === 'amount') {
                        setState({ ...state, chargeMode: 'custom', customAmount: e.target.value });
                      } else {
                        setState({ ...state, chargeMode: 'custom', customHours: e.target.value });
                      }
                    }}
                    className="glass-input"
                  />
                  {state.inputMode === 'hours' && parseFloat(state.customHours) > 0 && (
                    <p className="text-xs text-white/60 text-center">
                      {intl.formatMessage(
                        { id: 'checkout.estimated.cost' },
                        { amount: `${getEstimatedCostFromHours().toFixed(2)} ${cur}` },
                      )}
                    </p>
                  )}
                  {state.inputMode === 'amount' && parseFloat(state.customAmount) > 0 && (
                    <p className="text-xs text-white/60 text-center">~{getEstimatedHoursFromAmount()}</p>
                  )}
                  <p className="text-xs text-white/40 text-center">
                    <i className="ri-information-line"></i>{' '}
                    {intl.formatMessage({ id: 'checkout.autostop.hint' })}
                  </p>
                </div>
              )}
            </div>

            {/* SoC target */}
            <div
              className={`radio-card flex-col items-start ${state.chargeMode === 'soc' ? 'radio-card-selected' : 'radio-card-unselected'}`}
              onClick={() => setState({ ...state, chargeMode: 'soc' })}
            >
              <div className="flex items-center">
                <div className={`radio-dot ${state.chargeMode === 'soc' ? 'radio-dot-selected' : 'radio-dot-unselected'}`} />
                <span className="text-white text-sm">{intl.formatMessage({ id: 'checkout.charge.soc' })}</span>
              </div>
              {state.chargeMode === 'soc' && (
                <div className="w-full mt-3 pl-8 space-y-3">
                  <input
                    type="number"
                    min="1" max="100" step="1"
                    value={state.customSoC}
                    placeholder={intl.formatMessage({ id: 'checkout.soc.placeholder' })}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); setState({ ...state, chargeMode: 'soc', customSoC: e.target.value }); }}
                    className="glass-input"
                  />
                  <p className="text-xs text-white/40 text-center">
                    <i className="ri-information-line"></i>{' '}
                    {intl.formatMessage({ id: 'checkout.autostop.soc.hint' }, { percent: state.customSoC || '...' })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Auth info */}
          <p className="text-xs text-white/60 px-2 mb-3">QR Code Payment Preauthorization: charging stops before the total reaches the authorized amount; the charge cannot exceed it.</p>
        <p className="text-xs text-white/40 px-2 mb-4">
          {intl.formatMessage(
            { id: 'checkout.authinfo' },
            {
              authorzation_amount: (
                <b className="text-white/80">
                  {cur}{state.chargeMode === 'custom' && getSelectedAmount() ? getSelectedAmount().toFixed(2) : state.tariff_data?.authorization_amount}
                </b>
              ),
            },
          )}
        </p>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-brand-dark/90 backdrop-blur-lg border-t border-white/10 p-4 space-y-3">
        <div className="max-w-md mx-auto">
          {/* T&A */}
          <label className="flex items-start gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={state.taChecked}
              onChange={(e) => setState({ ...state, taChecked: e.target.checked, errMsg: null })}
              className="mt-1 w-4 h-4 rounded accent-accent"
            />
            <span className="text-white/60 text-xs">
              {state.terms_version ? 'I agree to The Bristol EV charging terms and authorize the temporary card hold and final session payment described above. Read the ' : intl.formatMessage({ id: 'checkout.accept.terms.prefix' })}{' '}
              <a
                href="#"
                className="text-accent underline"
                onClick={(e) => { e.preventDefault(); setState({ ...state, modalVisible: true }); }}
              >
                {intl.formatMessage({ id: 'checkout.accept.terms.linktext' })}
              </a>
            </span>
          </label>

          {state.errMsg && (
            <p className="text-danger text-sm text-center mb-2">{state.errMsg}</p>
          )}

          <button
            onClick={onCheckout}
            disabled={state.loading}
            className="btn-primary flex items-center justify-center gap-2"
          >
            {state.loading ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-flashlight-line"></i>
            )}
            {intl.formatMessage({ id: 'checkout.button.checkout' })}
          </button>
        </div>
      </div>

      {/* Terms modal */}
      {state.modalVisible && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setState({ ...state, modalVisible: false })}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Terms & Conditions</h3>
              <button onClick={() => setState({ ...state, modalVisible: false })} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="text-gray-800 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: state.payment_terms_conditions }}></div>
          </div>
        </div>
      )}
    </div>
  );
}
